import { Suspense, lazy } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import {
  SidebarProvider,
  SidebarInset,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import { AppSidebar } from "@/components/app-sidebar";
import { RoleGuard } from "@/components/common/RoleGuard";
import { ADMIN_SIDEBAR } from "@/routes";
import { STRINGS } from "@/constants/strings";

const AdminDashboardPage = lazy(
  () => import("@/pages/admin/AdminDashboardPage"),
);
const TenantsListPage = lazy(() => import("@/pages/admin/TenantsListPage"));
const TenantDetailPage = lazy(() => import("@/pages/admin/TenantDetailPage"));
const BundlesAdminPage = lazy(() => import("@/pages/admin/BundlesAdminPage"));
const BundleDetailAdminPage = lazy(
  () => import("@/pages/admin/BundleDetailAdminPage"),
);
const DevicesAdminPage = lazy(
  () => import("@/pages/admin/DevicesAdminPage"),
);
const DeviceDetailAdminPage = lazy(
  () => import("@/pages/admin/DeviceDetailAdminPage"),
);
const AuditLogAdminPage = lazy(() => import("@/pages/admin/AuditLogAdminPage"));
const S3RawDataBrowserPage = lazy(
  () => import("@/pages/admin/S3RawDataBrowserPage"),
);
const StreamVisualizerPage = lazy(
  () => import("@/pages/admin/StreamVisualizerPage"),
);

function PageTitle() {
  const location = useLocation();
  const current = ADMIN_SIDEBAR.find((item) =>
    item.path === "/"
      ? location.pathname === "/"
      : location.pathname.startsWith(item.path),
  );
  return current?.title ?? STRINGS.app.name;
}

export function AdminShell() {
  return (
    <RoleGuard allowedRoles={["superadmin"]}>
    <SidebarProvider>
      <AppSidebar shell="admin" items={ADMIN_SIDEBAR} />
      <SidebarInset>
        <header className="flex h-10 shrink-0 items-center gap-2 border-b px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 my-auto h-4" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbPage>
                  <PageTitle />
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </header>
        <main className="flex-1 p-4 md:p-6">
          <Suspense
            fallback={
              <div className="flex items-center justify-center py-12 text-muted-foreground">
                {STRINGS.admin.loading}
              </div>
            }
          >
            <Routes>
              <Route index element={<AdminDashboardPage />} />
              <Route path="tenants" element={<TenantsListPage />} />
              <Route path="tenants/:id" element={<TenantDetailPage />} />
              <Route path="bundles" element={<BundlesAdminPage />} />
              <Route path="bundles/:bundleId" element={<BundleDetailAdminPage />} />
              <Route path="devices" element={<DevicesAdminPage />} />
              <Route path="devices/:serialNumber" element={<DeviceDetailAdminPage />} />
              <Route path="audit" element={<AuditLogAdminPage />} />
              <Route path="s3-raw" element={<S3RawDataBrowserPage />} />
              <Route path="debug/stream-visualizer" element={<StreamVisualizerPage />} />
            </Routes>
          </Suspense>
        </main>
      </SidebarInset>
    </SidebarProvider>
    </RoleGuard>
  );
}
